// The small layout pieces the sales report is drawn with, in the admin theme,
// on the framework's controls (DropdownSelect, Checkbox).
import React from "react"
import { twMerge } from "tailwind-merge"
import Typography from "aldoo-ra/Typography"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Checkbox from "aldoo-ra/Checkbox"
import { url } from "aldoo-ra/CMS/url"

export const Field = ({ label, hint, children, className = "" }) => (
  <label className={twMerge("flex flex-col gap-1", className)}>
    {label && <span className="text-xs font-medium text-admin_text_secondary">{label}</span>}
    {children}
    {hint && <span className="text-[11px] text-admin_text_secondary">{hint}</span>}
  </label>
)

/** One value of a list: `value`, `onChange(value)`, `options: [{ value, label }]`. */
export const Select = ({ value, onChange, options = [], className = "" }) => {
  const opts = options.map((o) => ({ ...o, name: o.label ?? String(o.value) }))
  const selected = opts.find((o) => String(o.value) === String(value)) || null
  return (
    <div className={twMerge("inline-block align-middle", className)} data-value={String(value ?? "")}>
      <DropdownSelect
        variant="admin"
        options={opts}
        value={selected ? [selected] : []}
        onChange={(picked) => {
          const first = Array.isArray(picked) ? picked[0] : picked
          if (first) onChange?.(first.value)
        }}
        searchEnabled={false}
        showCheckbox={false}
        showSelectionTick
        enableResetButton={false}
        allowEmptySelection={false}
        styleOverrides={{
          button: "h-[38px] inline-flex items-center justify-between w-full rounded-md border border-admin_bordered px-3 bg-admin_paper text-sm font-normal text-admin_text outline-none focus:outline-none focus:border-admin_primary hover:bg-admin_paper transition-colors",
          selectedText: "text-admin_text",
        }}
      />
    </div>
  )
}

export const Toggle = ({ checked, onChange, label, "data-testid": testId }) => (
  <span data-testid={testId}>
    <Checkbox variant="admin_compact" isOn={!!checked} onChange={onChange} label={label} labelClassName="text-sm font-normal" className="select-none" />
  </span>
)

// `stackUntil` is the width the actions stay under the heading below: "md" by
// default, "xl" for a row of actions too wide to share the line sooner
const HEADER_ROW = {
  md: "flex flex-col md:flex-row md:items-center md:justify-between gap-3",
  xl: "flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3",
}

export const Section = ({ title, subtitle, actions, stackUntil = "md", children }) => (
  <section className="bg-admin_paper border border-admin_bordered rounded-lg p-4 flex flex-col gap-3">
    {(title || actions) && (
      // on a phone the actions drop under the heading, at the full width
      <div className={HEADER_ROW[stackUntil] || HEADER_ROW.md}>
        {title && (
          <div className="min-w-0">
            <Typography component="h3" variant="admin_h5" className="my-0">
              {title}
            </Typography>
            {subtitle && <div className="text-xs text-admin_text_secondary mt-0.5">{subtitle}</div>}
          </div>
        )}
        {actions}
      </div>
    )}
    {children}
  </section>
)

const initials = (name) => String(name || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?"

/** A CMS path ("images/…") on the CMS's static host; a url or a site path as it is. */
const imageSrc = (src) => (!src || String(src).startsWith("/") ? src : url(src))

/** An image that falls back to the name's initials when it has none or fails to load. */
export const Thumb = ({ src, name, className = "" }) => {
  const [failed, setFailed] = React.useState(false)
  React.useEffect(() => setFailed(false), [src])
  const showImage = !!src && !failed
  return (
    <div className={twMerge("bg-admin_neutral_1 overflow-hidden flex-shrink-0 flex items-center justify-center text-admin_text_secondary text-xs font-semibold select-none", className)}>
      {showImage ? <img src={imageSrc(src)} alt="" className="w-full h-full object-cover" onError={() => setFailed(true)} /> : initials(name)}
    </div>
  )
}
